/*
 * matchState — single source of truth for "what does this match look like
 * right now" across every UI that surfaces a match summary.
 *
 * Used by:
 *   - MatchScoring (the broadcast banner's leader + status pill)
 *   - BroadcastRoundSection (each match row's leader, margin, THRU)
 *   - any future surface that needs the same answer
 *
 * Why: we burned an afternoon on a bug where the score page showed
 * "Team Pat 2 UP" but the team view showed "AS" — two views, two
 * implementations of the same logic, predictably drifted. The fix is
 * to compute state in ONE place and consume it everywhere.
 *
 * Design:
 *   - Always derive leader + holes-won from the match_holes array.
 *     The API's `finalResultDisplay` string can be a stale or
 *     "running" label and shouldn't be trusted as the leader source.
 *   - Trust `finalResultDisplay` only when the match is truly final
 *     (status === 'final' OR finalResultType is set). In that case
 *     it carries closed-out nuances we don't want to re-implement
 *     ("3&2", "WIN", "HALVED").
 *   - winningTeamId IS populated when the match is final, so the
 *     leader for final matches comes from that, not the hole count.
 *
 * Tests live in matchState.test.ts.
 */

export interface DeriveMatchHole {
  holeNumber: number;
  winnerTeamId: string | null;
  isHalved: boolean;
}

export interface DeriveMatchInput {
  teamAId: string | null;
  teamBId: string | null;
  status: string;
  winningTeamId: string | null;
  finalResultType: string | null;
  finalResultDisplay: string | null;
  matchHoles: DeriveMatchHole[];
}

export interface DerivedMatchState {
  /** Which side is ahead. 'tie' covers both an all-square match in
   *  progress AND a final-halved result. Disambiguate with `isFinal`. */
  leader: 'a' | 'b' | 'tie';
  /** Display label for the margin badge. "" when not started. */
  marginLabel: string;
  /** Number of holes that have a recorded result (a winner OR halved). */
  played: number;
  /** Holes Team A has won outright. */
  aWon: number;
  /** Holes Team B has won outright. */
  bWon: number;
  /** Holes recorded as halved. */
  halved: number;
  /** 18 - played (clamped at 0). */
  remaining: number;
  /** Match is closed out (won or HALVED). */
  isFinal: boolean;
  /** Pill text for THRU column: "FINAL", "NOT STARTED", "THRU N". */
  thruLabel: string;
  /** A compact running label for places that show one string —
   *  e.g. "2 UP thru 7" or "FINAL · 3&2". Avoids re-stringifying
   *  elsewhere. */
  runningLabel: string;
}

const TOTAL_HOLES = 18;

export function deriveMatchState(input: DeriveMatchInput): DerivedMatchState {
  // 1. Count holes from match_holes — the authoritative per-hole record.
  let aWon = 0, bWon = 0, halved = 0, played = 0;
  for (const mh of input.matchHoles ?? []) {
    if (mh.winnerTeamId == null && !mh.isHalved) continue;
    played++;
    if (mh.isHalved) { halved++; continue; }
    if (mh.winnerTeamId === input.teamAId) aWon++;
    else if (mh.winnerTeamId === input.teamBId) bWon++;
  }
  const remaining = Math.max(0, TOTAL_HOLES - played);
  const isFinal = input.status === 'final' || input.finalResultType != null;

  // 2. Live leader + margin from the hole counts. `tie` is the live
  //    state when both sides have won the same number of holes.
  let leader: 'a' | 'b' | 'tie';
  let marginLabel: string;
  if (played === 0) {
    leader = 'tie';
    marginLabel = '';
  } else if (aWon > bWon) {
    leader = 'a';
    marginLabel = `${aWon - bWon} UP`;
  } else if (bWon > aWon) {
    leader = 'b';
    marginLabel = `${bWon - aWon} UP`;
  } else {
    leader = 'tie';
    marginLabel = 'AS';
  }

  // 3. For final matches, prefer the API's pretty string and use
  //    winningTeamId as the leader source.
  if (isFinal && input.finalResultDisplay) {
    const cleaned = input.finalResultDisplay
      .replace(/^Team \w+\s+/, '')
      .replace(/\s+thru.*$/i, '')
      .trim();
    if (cleaned) marginLabel = cleaned.toUpperCase();
    if (input.winningTeamId === input.teamAId) leader = 'a';
    else if (input.winningTeamId === input.teamBId) leader = 'b';
    else leader = 'tie';
  }

  // 4. THRU pill copy.
  const thruLabel = isFinal
    ? 'FINAL'
    : played === 0
      ? 'NOT STARTED'
      : `THRU ${played}`;

  // 5. Single running label for compact callers (e.g. event home cards).
  const runningLabel =
    played === 0
      ? 'Not started'
      : isFinal
        ? marginLabel ? `Final · ${marginLabel}` : 'Final'
        : `${marginLabel} thru ${played}`;

  return {
    leader,
    marginLabel,
    played,
    aWon,
    bWon,
    halved,
    remaining,
    isFinal,
    thruLabel,
    runningLabel,
  };
}
